import { useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  BrandLoading,
} from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

export interface PickerOption {
  id: string;
  label: string;
  secondary?: string;
}

export interface SearchPickerProps {
  value: string;
  placeholder: string;
  open: boolean;
  search: string;
  onOpenChange: (open: boolean) => void;
  onSearchChange: (next: string) => void;
  loading?: boolean;
  options: PickerOption[];
  onSelect: (id: string) => void;
  clearable?: boolean;
  onClear?: () => void;
}

/**
 * Generic search-picker shown beneath an input field. Used by the Payer
 * (Insurance / Corporate / Patient Package), TPA, and Service List pickers
 * inside billing dialogs. Mirrors Flutter `FieldType.SearchPicker`.
 *
 * Options are filtered client-side against the trimmed search query
 * (case-insensitive, against `label` and `secondary`). The caller is
 * responsible for fetching the option set — typically a React-Query hook
 * with debounced server-side search and a `keepPreviousData` placeholder.
 *
 * The dropdown panel composes the Signal DS `Command` family (cmdk-based).
 * `shouldFilter={false}` on the root because filtering already happens
 * above, against the caller-supplied `options` — Command must not filter
 * a second time against its own (untrimmed) search text.
 */
export function SearchPicker({
  value,
  placeholder,
  open,
  search,
  onOpenChange,
  onSearchChange,
  loading,
  options,
  onSelect,
  clearable,
  onClear,
}: SearchPickerProps) {
  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.secondary ?? '').toLowerCase().includes(q),
    );
  }, [options, search]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          onOpenChange(!open);
          onSearchChange('');
        }}
        className="w-full h-10 px-3 flex items-center justify-between bg-grey-100 rounded-md text-sm text-left"
      >
        <span
          className={cn(
            'truncate',
            value ? 'text-text-primary' : 'text-text-hint',
          )}
        >
          {value || placeholder}
        </span>
        <ChevronDown
          className="size-4 text-text-secondary shrink-0 ml-2"
          aria-hidden
        />
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label="Close picker"
            onClick={() => onOpenChange(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <div className="absolute z-20 left-0 right-0 top-full mt-1 rounded-md border border-border-default bg-surface-card shadow-lg overflow-hidden">
            <Command shouldFilter={false}>
              <CommandInput
                autoFocus
                value={search}
                onValueChange={onSearchChange}
                placeholder="Search…"
              />
              <CommandList className="max-h-70 overflow-y-auto scrollbar-hide py-1">
                {clearable && (
                  <CommandItem
                    value="__clear__"
                    className="text-status-error"
                    onSelect={() => {
                      onClear?.();
                      onOpenChange(false);
                    }}
                  >
                    Clear selection
                  </CommandItem>
                )}
                {loading && filtered.length === 0 ? (
                  <BrandLoading size={32} label="Loading…" />
                ) : (
                  <>
                    <CommandEmpty>
                      <p className="text-sm text-text-muted text-center py-4">
                        No matches
                      </p>
                    </CommandEmpty>
                    <CommandGroup>
                      {filtered.map((o) => (
                        <CommandItem
                          key={o.id}
                          value={o.id}
                          onSelect={(id) => {
                            onSelect(id);
                            onOpenChange(false);
                          }}
                        >
                          <span className="flex w-full items-center justify-between gap-3 truncate">
                            <span className="truncate">{o.label}</span>
                            {o.secondary && (
                              <span className="text-xs text-text-muted shrink-0">
                                {o.secondary}
                              </span>
                            )}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </div>
        </>
      )}
    </div>
  );
}
